import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Re-renders small PDP fragments from Horizon's product-select response.
 * This keeps Nikura-specific variant-dependent UI in sync without replacing
 * Horizon's native product form, price, or variant picker infrastructure.
 */
class EqPdpVariantFragment extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    super.disconnectedCallback();
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const key = this.dataset.fragmentKey;
        if (!key) return;

        const next = detail.html.querySelector(`eq-pdp-variant-fragment[data-fragment-key="${key}"]`);
        if (!next) return;

        this.innerHTML = next.innerHTML;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[eq-pdp] Product fragment update failed:', error);
      });
  };
}

class EqPdpCountdown extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#update();
    this.interval = window.setInterval(this.#update, 1000);
  }

  disconnectedCallback() {
    if (this.interval) window.clearInterval(this.interval);
    super.disconnectedCallback();
  }

  #update = () => {
    const endTimestamp = Number(this.dataset.endTimestamp || 0) * 1000;
    if (!endTimestamp) return;

    const remaining = endTimestamp - Date.now();
    if (remaining <= 0) {
      if (this.interval) window.clearInterval(this.interval);
      this.hidden = true;
      return;
    }

    const totalMinutes = Math.floor(remaining / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    const daysElement = this.querySelector('[data-days]');
    const hoursElement = this.querySelector('[data-hours]');
    const minutesElement = this.querySelector('[data-minutes]');

    if (daysElement) daysElement.textContent = String(days);
    if (hoursElement) hoursElement.textContent = String(hours);
    if (minutesElement) minutesElement.textContent = String(minutes);
  };
}

class EqPdpDemandBadge extends Component {
  connectedCallback() {
    super.connectedCallback();

    const duration = Number(this.dataset.dismissAfter || 0);
    if (duration > 0) {
      this.timer = window.setTimeout(() => this.dismiss(), duration * 1000);
    }
  }

  disconnectedCallback() {
    if (this.timer) window.clearTimeout(this.timer);
    super.disconnectedCallback();
  }

  dismiss() {
    this.hidden = true;
    if (this.timer) window.clearTimeout(this.timer);
  }
}

/**
 * Applies the live Nikura stock cap to Horizon's native quantity selector.
 * Horizon continues to own quantity UI/cart syncing; this component only
 * supplies the per-variant inventory maximum that the live PDP enforces.
 */
class EqPdpInventoryControl extends Component {
  #abortController = null;
  #inventoryMap = {};

  connectedCallback() {
    super.connectedCallback();

    const mapScript = this.querySelector('[data-inventory-map]');
    try {
      this.#inventoryMap = JSON.parse(mapScript?.textContent || '{}');
    } catch {
      this.#inventoryMap = {};
    }

    // Fresh controller per connection so a reconnected instance re-registers its listeners.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });

    this.#applyConstraints(this.dataset.currentVariantId);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    super.disconnectedCallback();
  }

  #getProductForm() {
    return this.section?.querySelector('product-form-component') || null;
  }

  #applyConstraints(variantId) {
    if (!variantId) return;

    const variant = this.#inventoryMap[String(variantId)];
    if (!variant) return;

    const productForm = this.#getProductForm();
    const quantitySelector = productForm?.refs?.quantitySelector;
    const quantityInput = quantitySelector?.refs?.quantityInput;
    if (!productForm || !quantitySelector || !quantityInput) return;

    const isGiftCard = this.dataset.giftCard === 'true';
    const shouldCapInventory =
      !isGiftCard && variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny';
    const maximum = shouldCapInventory ? Math.max(0, Number(variant.inventory_quantity || 0)) : null;

    quantitySelector.updateConstraints?.(
      quantityInput.min || '1',
      maximum === null ? null : String(maximum),
      quantityInput.step || '1'
    );

    const cartQuantity = Number(quantityInput.getAttribute('data-cart-quantity') || 0);
    const minimum = Number(quantityInput.min || 1);
    const remaining = maximum === null ? Number.POSITIVE_INFINITY : maximum - cartQuantity;
    const addToCart = productForm.refs?.addToCartButtonContainer;

    if (variant.available === false || remaining < minimum) {
      addToCart?.disable?.();
    } else {
      addToCart?.enable?.();
    }
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const variantId = detail?.resource?.id;
        if (!variantId) return;
        this.dataset.currentVariantId = String(variantId);

        // Let Horizon finish morphing the quantity/add button first, then apply
        // Nikura's inventory ceiling to the new native controls.
        requestAnimationFrame(() => requestAnimationFrame(() => this.#applyConstraints(String(variantId))));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[eq-pdp] Inventory sync failed:', error);
      });
  };

  #handleCartUpdate = () => {
    const variantId = this.dataset.currentVariantId;
    requestAnimationFrame(() => this.#applyConstraints(variantId));
  };
}

/** Live-style delivery countdown layered on the server-computed delivery rule. */
class EqPdpDeliveryEstimate extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#update();
    if (this.#hasPendingCutoff()) this.interval = window.setInterval(this.#update, 1000);
  }

  disconnectedCallback() {
    if (this.interval) window.clearInterval(this.interval);
    super.disconnectedCallback();
  }

  #hasPendingCutoff() {
    const cutoff = Number(this.dataset.cutoffTimestamp || 0) * 1000;
    return cutoff > Date.now();
  }

  #update = () => {
    const cutoff = Number(this.dataset.cutoffTimestamp || 0) * 1000;
    if (!cutoff) return;

    const difference = cutoff - Date.now();
    const countdown = this.querySelector('[data-countdown]');

    if (difference <= 0) {
      this.querySelector('[data-before-cutoff]')?.setAttribute('hidden', '');
      this.querySelector('[data-after-cutoff]')?.removeAttribute('hidden');
      if (this.interval) window.clearInterval(this.interval);
      return;
    }

    if (!countdown) return;
    const totalSeconds = Math.floor(difference / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    countdown.textContent = `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  };
}

/** Loads Clearpay/Afterpay only when the PDP placement block is present. */
class EqPdpAfterpay extends Component {
  static loadPromise;

  connectedCallback() {
    super.connectedCallback();
    this.#ensureScript();
  }

  #ensureScript() {
    if (customElements.get('afterpay-placement')) return Promise.resolve();
    if (EqPdpAfterpay.loadPromise) return EqPdpAfterpay.loadPromise;

    EqPdpAfterpay.loadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-eq-afterpay]');
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js.afterpay.com/afterpay-1.x.js';
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.dataset.eqAfterpay = 'true';
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    return EqPdpAfterpay.loadPromise;
  }
}

/**
 * Horizon's product form always adds the main variant itself. When Nikura upsells are checked, they
 * are added in a second request once that native add has succeeded, carrying the live
 * `_upsell-type = misti-upsell` contract — so a main product the cart refuses never leaves the
 * add-ons in the cart on their own.
 */
class EqPdpUpsells extends Component {
  #abortController = null;
  #activeDots = 0;
  #dotSyncFrame = 0;
  /** @type {(() => void) | null} */
  #stopAwaitingMainAdd = null;

  connectedCallback() {
    super.connectedCallback();
    // Fresh controller per connection so a reconnected instance re-registers its listeners.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.section = this.closest('.shopify-section, dialog');
    const { signal } = this.#abortController;
    // Horizon's `on:submit` runs from a capture listener on `document`; capturing on `window` runs first,
    // so the listener for the main add's event is in place before Horizon dispatches it.
    window.addEventListener('submit', this.#handleSubmit, { capture: true, signal });
    this.addEventListener('slideshow:select', this.#handleSlideshowSelect, { signal });
    window.addEventListener('resize', this.#requestDotSync, { signal });
    this.#requestDotSync();
  }

  /** Coalesces resize bursts so the dot count is measured once a frame, not once an event. */
  #requestDotSync = () => {
    if (this.#dotSyncFrame) return;

    this.#dotSyncFrame = requestAnimationFrame(() => {
      this.#dotSyncFrame = 0;
      if (this.isConnected) this.#syncDotCount();
    });
  };

  /**
   * Shows one dot per scroll position rather than per slide.
   *
   * Slideshow renders a dot for every slide, but with several cards in view the last few can
   * never lead the row, so their dots are unreachable. The count depends on how many cards fit,
   * which only the layout knows — hence measuring rather than a Liquid count.
   */
  #syncDotCount = () => {
    const items = this.querySelectorAll('.slideshow-controls__dots > li');
    const track = this.querySelector('slideshow-slides');
    const slide = this.querySelector('.eq-pdp-upsells__slide');
    if (items.length === 0 || !track || !slide) return;

    const step = slide.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || '0');
    const visible = step > 0 ? Math.round(track.clientWidth / step) : 1;
    const positions = Math.max(1, Math.min(items.length, items.length - visible + 1));

    items.forEach((item, i) => item.toggleAttribute('hidden', i >= positions));
    this.#activeDots = positions;
  };

  /** Keeps a dot marked once the track scrolls past the last reachable position. */
  #handleSlideshowSelect = (event) => {
    const index = event.detail?.index;
    if (typeof index !== 'number') return;

    const dots = this.querySelectorAll('.slideshow-controls__dots .slideshow-control');
    if (dots.length === 0) return;

    const last = (this.#activeDots || dots.length) - 1;
    const active = Math.min(index, last);
    dots.forEach((dot, i) => dot.setAttribute('aria-selected', `${i === active}`));
  };

  disconnectedCallback() {
    cancelAnimationFrame(this.#dotSyncFrame);
    this.#dotSyncFrame = 0;
    this.#stopAwaitingMainAdd?.();
    this.#abortController?.abort();
    super.disconnectedCallback();
  }

  #getProductForm() {
    return this.section?.querySelector('product-form-component') || null;
  }

  #selectedUpsells() {
    return Array.from(this.querySelectorAll('[data-upsell-variant-id]:checked'));
  }

  /**
   * Runs before Horizon's own submit handler and leaves it alone: it only arms a one-off listener for
   * the add Horizon is about to announce. Horizon may announce it later (a click during a variant
   * change is queued) or not at all (a refused quantity), hence the time limit.
   *
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    const selected = this.#selectedUpsells();
    if (selected.length === 0) return; // Native Horizon path remains untouched.

    const productForm = this.#getProductForm();
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !productForm || !productForm.contains(form)) return;

    this.#stopAwaitingMainAdd?.();

    const onMainAdd = (/** @type {any} */ cartEvent) => {
      stop();
      this.#addUpsellsAfter(cartEvent.promise, productForm, selected);
    };
    const timer = window.setTimeout(() => stop(), 10000);
    const stop = () => {
      productForm.removeEventListener(StandardEvents.cartLinesUpdate, onMainAdd);
      window.clearTimeout(timer);
      this.#stopAwaitingMainAdd = null;
    };

    productForm.addEventListener(StandardEvents.cartLinesUpdate, onMainAdd);
    this.#stopAwaitingMainAdd = stop;
  };

  /**
   * @param {Promise<any> | undefined} mainAdd - The native add's promise.
   * @param {HTMLElement} productForm
   * @param {HTMLInputElement[]} selected - The upsells checked when the shopper submitted.
   */
  async #addUpsellsAfter(mainAdd, productForm, selected) {
    try {
      const result = await mainAdd;
      if (!result || result.detail?.didError) return;
    } catch {
      return;
    }

    const quantitySelector = /** @type {any} */ (productForm).refs?.quantitySelector;
    const items = [
      ...selected.map((input) => {
        const item = {
          id: Number(input.dataset.upsellVariantId),
          quantity: 1,
          properties: { '_upsell-type': 'misti-upsell' },
        };
        if (input.dataset.sellingPlanId) item.selling_plan = Number(input.dataset.sellingPlanId);
        return item;
      }),
    ];

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : null)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    productForm.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        const message = result.message || result.description || 'Add to cart failed';
        productForm.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID', detail: result }));
        deferred.reject(new Error(message));
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'eq-pdp-upsells',
          sourceId: this.id || 'eq-pdp-upsells',
          itemCount: items.reduce((total, item) => total + item.quantity, 0),
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });

      selected.forEach((input) => { input.checked = false; });
      const minimum = quantitySelector?.refs?.quantityInput?.min || productForm.dataset.quantityDefault || '1';
      quantitySelector?.setValue?.(minimum);
      quantitySelector?.updateButtonStates?.();
    } catch (error) {
      const message = error?.message || 'Network error during add to cart';
      productForm.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
      deferred.reject(error);
    }
  }
}


class EqPdpAvailabilityActions extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.section = this.closest('.shopify-section, dialog');
    this.#setNativeButtonsHidden(this.dataset.hideNativeButtons === 'true');
  }

  disconnectedCallback() {
    this.#setNativeButtonsHidden(false);
    super.disconnectedCallback();
  }

  #setNativeButtonsHidden(hidden) {
    const buttons = this.section?.querySelector('product-form-component .product-form-buttons');
    if (!buttons) return;
    if (hidden) {
      buttons.setAttribute('hidden', '');
      buttons.dataset.eqAvailabilityHidden = 'true';
    } else if (buttons.dataset.eqAvailabilityHidden === 'true') {
      buttons.removeAttribute('hidden');
      delete buttons.dataset.eqAvailabilityHidden;
    }
  }
}
if (!customElements.get('eq-pdp-variant-fragment')) customElements.define('eq-pdp-variant-fragment', EqPdpVariantFragment);
if (!customElements.get('eq-pdp-countdown')) customElements.define('eq-pdp-countdown', EqPdpCountdown);
if (!customElements.get('eq-pdp-demand-badge')) customElements.define('eq-pdp-demand-badge', EqPdpDemandBadge);
if (!customElements.get('eq-pdp-inventory-control')) customElements.define('eq-pdp-inventory-control', EqPdpInventoryControl);
if (!customElements.get('eq-pdp-delivery-estimate')) customElements.define('eq-pdp-delivery-estimate', EqPdpDeliveryEstimate);
if (!customElements.get('eq-pdp-afterpay')) customElements.define('eq-pdp-afterpay', EqPdpAfterpay);
if (!customElements.get('eq-pdp-upsells')) customElements.define('eq-pdp-upsells', EqPdpUpsells);
if (!customElements.get('eq-pdp-availability-actions')) customElements.define('eq-pdp-availability-actions', EqPdpAvailabilityActions);
